
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  layout: 'guide',
  description: 'Learn how to subscribe users to recurring Consumer Report generation with Servicing'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Servicing' subtitle='Receive recurring Consumer Report updates for a user after origination' mdxType="Header" />
    <p>{`Servicing keeps a Consumer Report current after a loan has been originated. You subscribe a user once, and Plaid regenerates their report on a recurring cadence and notifies you each time new data is available.`}</p>
    <p>{`Reports are delivered through the same `}<inlineCode parentName="p">{`/cra/check_report/base_report/get`}</inlineCode>{` and `}<inlineCode parentName="p">{`/cra/check_report/income_insights/get`}</inlineCode>{` endpoints used at origination.`}</p>
    <Callout mdxType="Callout">
      <p>{`Servicing became generally available on August 20, 2026, replacing Cash Flow Updates. Integrations built on the earlier `}<inlineCode parentName="p">{`/cra/monitoring_insights/*`}</inlineCode>{` endpoints should see `}<a parentName="p" {...{
          "href": "/docs/check/migrate-from-cash-flow-updates/"
        }}>{`Migrate from Cash Flow Updates`}</a>{`. Those endpoints are deprecated and will stop functioning on August 20, 2027.`}</p>
    </Callout>
    <h4 {...{
      "id": "before-you-begin"
    }}>{`Before you begin`}</h4>
    <p>{`Servicing requires an existing Plaid Check integration. If you have not onboarded users to Plaid Check yet, start with `}<a parentName="p" {...{
        "href": "/docs/check/add-to-app/"
      }}>{`Add Consumer Report to your app`}</a>{`.`}</p>
    <p>{`You will also need Servicing enabled on your account. To request access, contact your account manager.`}</p>
    <h4 {...{
      "id": "integration-overview"
    }}>{`Integration overview`}</h4>
    <ol>
      <li parentName="ol">{`Configure a webhook listener and register its URL with Plaid.`}</li>
      <li parentName="ol">{`Create a subscription for a user, specifying the products to include in each report.`}</li>
      <li parentName="ol">{`Receive a `}<inlineCode parentName="li">{`CRA_REPORT_UPDATED`}</inlineCode>{` webhook each time Plaid generates a new report for that user.`}</li>
      <li parentName="ol">{`Fetch the updated report by calling Consumer Report `}<inlineCode parentName="li">{`/get`}</inlineCode>{` endpoints.`}</li>
    </ol>
    <p>{`When you subscribe, every CRA Item the user has linked is included, and Items the user links later are picked up automatically. Each generation aggregates across all healthy Items into a single report; if some Items fail, the report is still generated from the Items that succeeded.`}</p>
    <p>{`Duplicate Items are deduplicated: if Plaid detects multiple copies of the same bank connection, only one is included in the report.`}</p>
    <p>{`A report can include a maximum of 25 Items.`}</p>
    <h4 {...{
      "id": "configure-webhook-listener"
    }}>{`Configure webhook listener`}</h4>
    <p>{`Manage webhook destinations for Servicing from the Plaid Dashboard: on a CRA-enabled account, go to `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/webhooks"
      }}>{`Webhooks`}</a>{` and configure a destination for the `}<strong parentName="p">{`CRA Report Updated`}</strong>{` event.`}</p>
    <p>{`Configuring the destination in the Dashboard lets you move every active subscription to a new URL in a single action, rather than updating subscriptions one at a time. If you prefer to configure this per subscription (e.g. to use different webhook URLs for different environments), pass an override URL in the `}<inlineCode parentName="p">{`webhook`}</inlineCode>{` field when you create the subscription.`}</p>
    <h4 {...{
      "id": "create-a-subscription"
    }}>{`Create a subscription`}</h4>
    <p>{`Call `}<inlineCode parentName="p">{`/cra/servicing/subscription/create`}</inlineCode>{` with a `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` and the products to include in each report. Currently, supported products are `}<inlineCode parentName="p">{`cra_base_report`}</inlineCode>{` and `}<inlineCode parentName="p">{`cra_income_insights`}</inlineCode>{`, the only valid `}<inlineCode parentName="p">{`cadence`}</inlineCode>{` is `}<inlineCode parentName="p">{`DAILY`}</inlineCode>{`, and the only valid `}<inlineCode parentName="p">{`scope`}</inlineCode>{` is `}<inlineCode parentName="p">{`CLIENT_USER`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "#Subscribe a user to Servicing",
        "#Subscribe": true,
        "a": true,
        "user": true,
        "to": true,
        "Servicing": true
      }}>{`curl -X POST https://sandbox.plaid.com/cra/servicing/subscription/create \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "user_id": "usr_9nSp2KuZ2x4JDw",
  "scope": "CLIENT_USER",
  "cadence": "DAILY",
  "products": [
    { "product": "cra_base_report", "version": "V1" },
    { "product": "cra_income_insights", "version": "V2" }
  ]
}'
`}</code></pre>
    <p>{`The response returns a `}<inlineCode parentName="p">{`subscription_id`}</inlineCode>{` in the format `}<inlineCode parentName="p">{`cra-sub-<env>-<uuid>`}</inlineCode>{`. Store this value.`}</p>
    <p>{`Reports are generated once every 24 hours; the first report is generated within 24 hours of the subscription being created.`}</p>
    <h4 {...{
      "id": "handle-the-webhook"
    }}>{`Handle the webhook`}</h4>
    <p>{`Each generation fires a single `}<a parentName="p" {...{
        "href": "/docs/api/products/check-legacy/#cra_report_updated"
      }}><inlineCode parentName="a">{`CRA_REPORT_UPDATED`}</inlineCode></a>{` webhook for the user, covering every product on the subscription. There is no separate webhook for failures: success, partial success, and total failure are all delivered as the same event. If the `}<inlineCode parentName="p">{`error_code`}</inlineCode>{` on the webhook is absent or `}<inlineCode parentName="p">{`null`}</inlineCode>{`, at least one product generated successfully.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "switcher=false #Successful generation",
        "switcher": "false",
        "#Successful": true,
        "generation": true
      }}>{`{
  "webhook_type": "CRA_REPORT",
  "webhook_code": "CRA_REPORT_UPDATED",
  "user_id": "usr_9nSp2KuZ2x4JDw",
  "client_user_id": "your-db-id-3b24110",
  "scope": "CLIENT_USER",
  "successful_products": [
    { "product": "cra_base_report", "version": "V1" },
    { "product": "cra_income_insights", "version": "V2" }
  ],
  "failed_products": [],
  "generated_time": "2026-08-21T14:03:22Z",
  "error_code": null,
  "report_id": "cra-report-production-6f8b1e0c-4d2a-4c7e-9a3b-2f1d8e5a7c90",
  "environment": "production"
}
`}</code></pre>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "switcher=false #Total failure",
        "switcher": "false",
        "#Total": true,
        "failure": true
      }}>{`{
  "webhook_type": "CRA_REPORT",
  "webhook_code": "CRA_REPORT_UPDATED",
  "user_id": "usr_9nSp2KuZ2x4JDw",
  "client_user_id": "your-db-id-3b24110",
  "scope": "CLIENT_USER",
  "successful_products": [],
  "failed_products": [
    { "product": "cra_base_report", "version": "V1" },
    { "product": "cra_income_insights", "version": "V2" }
  ],
  "generated_time": null,
  "error_code": "PRODUCT_GENERATION_FAILED",
  "report_id": null,
  "environment": "production"
}
`}</code></pre>
    <Callout mdxType="Callout">
      <p>{`A subscription that repeatedly fails with `}<inlineCode parentName="p">{`PRODUCT_GENERATION_FAILED`}</inlineCode>{` indicates that no report can be generated from the user's current Items. Check the user's Items via `}<inlineCode parentName="p">{`/user/items/get`}</inlineCode>{`, prompt the user to relink if needed, or delete the subscription.`}</p>
    </Callout>
    <h4 {...{
      "id": "fetch-the-updated-report"
    }}>{`Fetch the updated report`}</h4>
    <p>{`For each product in the webhook's `}<inlineCode parentName="p">{`successful_products`}</inlineCode>{` field, call that product's `}<inlineCode parentName="p">{`/get`}</inlineCode>{` endpoint: `}<inlineCode parentName="p">{`/cra/check_report/base_report/get`}</inlineCode>{` for `}<inlineCode parentName="p">{`cra_base_report`}</inlineCode>{`, and `}<inlineCode parentName="p">{`/cra/check_report/income_insights/get`}</inlineCode>{` for `}<inlineCode parentName="p">{`cra_income_insights`}</inlineCode>{`. Pass the following parameters:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`report_id`}</inlineCode>{`, taken from the `}<inlineCode parentName="p">{`CRA_REPORT_UPDATED`}</inlineCode>{` webhook. Without it, the endpoints return the most recently generated report for the user, which could be a report from an origination `}<inlineCode parentName="p">{`/cra/check_report/create`}</inlineCode>{` call or another subscription rather than the one this webhook is about.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`consumer_report_permissible_purpose`}</inlineCode>{`, set to a servicing-compliant purpose: either `}<inlineCode parentName="p">{`ACCOUNT_REVIEW_CREDIT`}</inlineCode>{`, for the review or collection of an account under FCRA Section 604(a)(3)(A), or `}<inlineCode parentName="p">{`WRITTEN_INSTRUCTION_OTHER`}</inlineCode>{`, in accordance with the written instructions of the consumer under FCRA Section 604(a)(2).`}</p>
      </li>
    </ul>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "#Fetch a report generated by a subscription",
        "#Fetch": true,
        "a": true,
        "report": true,
        "generated": true,
        "by": true,
        "subscription": true
      }}>{`curl -X POST https://sandbox.plaid.com/cra/check_report/base_report/get \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "user_id": "usr_9nSp2KuZ2x4JDw",
  "report_id": "cra-report-production-6f8b1e0c-4d2a-4c7e-9a3b-2f1d8e5a7c90",
  "consumer_report_permissible_purpose": "ACCOUNT_REVIEW_CREDIT"
}'
`}</code></pre>
    <Callout warning mdxType="Callout">
      <p>{`Reports generated by a subscription can be retrieved only for the two servicing-compliant purposes above. Decisioning purposes accepted at origination, such as `}<inlineCode parentName="p">{`EXTENSION_OF_CREDIT`}</inlineCode>{`, are not accepted for Servicing. To generate a report you can use for a credit decision, call `}<inlineCode parentName="p">{`/cra/check_report/create`}</inlineCode>{` with a `}<a parentName="p" {...{
          "href": "/docs/api/products/check-legacy/#cra-check_report-create-request-consumer-report-permissible-purpose"
        }}>{`decisioning permissible purpose`}</a>{` instead.`}</p>
    </Callout>
    <p>{`Currently, only `}<inlineCode parentName="p">{`/cra/check_report/base_report/get`}</inlineCode>{` and `}<inlineCode parentName="p">{`/cra/check_report/income_insights/get`}</inlineCode>{` can retrieve a report generated by a subscription. Other Consumer Report endpoints, including `}<inlineCode parentName="p">{`/cra/check_report/pdf/get`}</inlineCode>{`, are not supported.`}</p>
    <h4 {...{
      "id": "manage-subscriptions"
    }}>{`Manage subscriptions`}</h4>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Endpoint`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/cra/servicing/subscription/get`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Retrieve the current configuration of a subscription.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/cra/servicing/subscription/list`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`List a user's subscriptions, to recover a `}<inlineCode parentName="td">{`subscription_id`}</inlineCode>{` you did not persist.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/cra/servicing/subscription/update`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Change the products or webhook override on an active subscription.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`/cra/servicing/subscription/delete`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`End the subscription. No further reports are generated and no webhooks fire.`}</td>
        </tr>
      </tbody>
    </table>
    <h4 {...{
      "id": "testing-in-sandbox"
    }}>{`Testing in Sandbox`}</h4>
    <p>{`Use `}<inlineCode parentName="p">{`/sandbox/cra/servicing/simulate`}</inlineCode>{` to trigger a report generation for a subscription without waiting. The endpoint generates a report, returns its `}<inlineCode parentName="p">{`report_id`}</inlineCode>{`, and fires the resulting `}<inlineCode parentName="p">{`CRA_REPORT_UPDATED`}</inlineCode>{` webhook.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "#Simulate a report generation in Sandbox",
        "#Simulate": true,
        "a": true,
        "report": true,
        "generation": true,
        "in": true,
        "Sandbox": true
      }}>{`curl -X POST https://sandbox.plaid.com/sandbox/cra/servicing/simulate \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "subscription_id": "cra-sub-sandbox-8f2b1c0a-3d4e-5f60-7a8b-9c0d1e2f3a4b",
  "options": {
    "transactions_end_date": "2026-09-01"
  }
}'
`}</code></pre>
    <p>{`To simulate a report changing over time, advance `}<inlineCode parentName="p">{`options.transactions_end_date`}</inlineCode>{` on successive calls. To simulate failures, use `}<inlineCode parentName="p">{`options.error_webhook_code`}</inlineCode>{`.`}</p>
    <h4 {...{
      "id": "billing"
    }}>{`Billing`}</h4>
    <p>{`Servicing is billed per subscribed user. While a subscription is active, fetching that user's Base Report or Income Insights does not incur a separate report charge. For details, see `}<a parentName="p" {...{
        "href": "/docs/account/billing/#plaid-check-fee-model"
      }}>{`Plaid Check fee model`}</a>{`.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "before-you-begin",
        level: 4,
        title: "Before you begin",
      },
{
        id: "integration-overview",
        level: 4,
        title: "Integration overview",
      },
{
        id: "configure-webhook-listener",
        level: 4,
        title: "Configure webhook listener",
      },
{
        id: "create-a-subscription",
        level: 4,
        title: "Create a subscription",
      },
{
        id: "handle-the-webhook",
        level: 4,
        title: "Handle the webhook",
      },
{
        id: "fetch-the-updated-report",
        level: 4,
        title: "Fetch the updated report",
      },
{
        id: "manage-subscriptions",
        level: 4,
        title: "Manage subscriptions",
      },
{
        id: "testing-in-sandbox",
        level: 4,
        title: "Testing in Sandbox",
      },
{
        id: "billing",
        level: 4,
        title: "Billing",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
